import { computed, nextTick, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import { get as lodashGet } from 'lodash';
import { useStore } from '@/store';
import ColumnsService from '@/services/columns';
import { $emitter } from '@/services/emitter';
import {
  useAcl,
  useI18n,
  useUrl,
  useMainMenuComplexTabs,
  useTableQuery,
} from '@/composables';

export enum MainMenuLayoutType {
  tab = 0,
  tabCustom,
  delimiter,
}

export interface MainMenuLayoutTab extends Bemob.ListItemBase<MainMenuLayoutTab> {
  type: MainMenuLayoutType;
}

export const useMainMenuGrouping = () => {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const { t } = useI18n();
  const { canAccessPage, isUserOrMemberAdmin } = useAcl();
  const {
    computedType,
    isReport,
    isHidden,
    getLink,
    conversionsLink,
    errorsLink,
    postbacksLink,
    getDashboardLink,
    getLivesLink,
    isDashboard,
  } = useUrl();
  const {
    tabsMeta,
    complexTabsData,
    complexTabs,
    customGroupsShowCondition,
    campaignAsFirstTableFilterCondition,
    tabsPlainTabsDataExclude,
  } = useMainMenuComplexTabs();

  const menuOpened = ref<{ [type: string]: boolean }>({});

  const mainMenuLayoutTypeEnum = {
    tab: MainMenuLayoutType.tab,
    tabCustom: MainMenuLayoutType.tabCustom,
    delimiter: MainMenuLayoutType.delimiter,
  };

  const handleMenuClick = (
    event: MouseEvent | undefined,
    link: RouteLocationRaw | (() => RouteLocationRaw),
    saveReportSimpleColumns: boolean = false,
  ) => {
    const which = event ? event.which : 1;

    if (which) {
      let resolvedLink = link;
      if (typeof resolvedLink === 'function') {
        resolvedLink = resolvedLink();
      }

      const currentType = ColumnsService.type;
      const nextType = ColumnsService.getType(resolvedLink);

      if (currentType === nextType) {
        return;
      }

      store.setLoading(true);
      store.setMenuOverride(nextType);

      $emitter.emit('freeze-table');

      setTimeout(() => {
        router.push(resolvedLink);

        setTimeout(() => {
          nextTick(() => {
            $emitter.emit('get-report');
          });
        }, 10);
      });
    }

    return false;
  };

  const handleComplexMenuClickCb = (data: { isLive?: boolean; name: string }) => {
    const linkCb = data.isLive
      ? () => getLivesLink(data.name)
      : () =>
          getLink(data.name, {
            isReport: isReport.value,
          });

    return linkCb;
  };

  const handleComplexMenuClick = (
    event: MouseEvent,
    data: { isLive?: boolean; name: string },
  ) => {
    const linkCb = handleComplexMenuClickCb(data);
    handleMenuClick(event, linkCb, true);
  };

  const mainPlainLayout = computed((): MainMenuLayoutTab[] =>
    lodashGet(complexTabsData.value, 'mainPlain', []).map(
      (data) =>
        ({
          type: MainMenuLayoutType.tab,
          key: data.name,
          icon: data.icon,
          title: data.title,
          active: data.name === computedType.value,
          showCondition: !isHidden(data.name),
          click: (event: MouseEvent) => {
            handleMenuClick(event, () =>
              getLink(data.name, {
                isReport: isReport.value,
              }),
            );
          },
          link: getLink(data.name, {
            isReport: isReport.value,
          }),
        }) as MainMenuLayoutTab,
    ),
  );

  const conversionsTabLayout = computed((): MainMenuLayoutTab[] => {
    const result: MainMenuLayoutTab = {
      type: MainMenuLayoutType.tab,
      key: 'conversions',
      icon: tabsMeta.value['conversions'].icon,
      title: t('Conversions'),
      active: computedType.value === 'conversions',
      link: conversionsLink.value,
      showCondition:
        (!store.user.hideProfit || store.user.showConversions)
        && !!complexTabsData.value.conversions,
      click: (event: MouseEvent) => {
        handleMenuClick(event, conversionsLink.value);
      },
    };

    return [result];
  });

  const errorsTabLayout = computed((): MainMenuLayoutTab[] => {
    const result: MainMenuLayoutTab = {
      type: MainMenuLayoutType.tab,
      key: 'errors',
      icon: tabsMeta.value['errors'].icon,
      title: t('Errors'),
      active: computedType.value === 'errors',
      showCondition: !!complexTabsData.value.errors,
      link: errorsLink.value,
      click: (event: MouseEvent) => {
        handleMenuClick(event, errorsLink.value);
      },
    };

    return [result];
  });

  const postbacksTabLayout = computed((): MainMenuLayoutTab[] => {
    const result: MainMenuLayoutTab = {
      type: MainMenuLayoutType.tab,
      key: 'postbacks',
      icon: tabsMeta.value['postbacks'].icon,
      title: t('Postbacks'),
      active: computedType.value === 'postbacks',
      showCondition:
        !store.user.hideProfit
        && !!complexTabsData.value.postbacks
        && isUserOrMemberAdmin.value,
      link: postbacksLink.value,
      click: (event: MouseEvent) => {
        handleMenuClick(event, postbacksLink.value);
      },
    };

    return [result];
  });

  const dashboardTabLayout = computed((): MainMenuLayoutTab[] => {
    const result: MainMenuLayoutTab = {
      type: MainMenuLayoutType.tab,
      key: 'dashboard',
      icon: 'fas fa-tachometer-alt',
      title: t('Dashboard stub'),
      active: route.path.includes('/app/dashboard/'),
      showCondition: true,
      link: getDashboardLink(),
      click: () => {
        router.push(getDashboardLink());
      },
    };

    return [result];
  });

  const complexTabsLayout = computed((): MainMenuLayoutTab[] =>
    complexTabs.value.map(
      (complexTab) =>
        ({
          type: MainMenuLayoutType.tab,
          key: complexTab.name,
          icon: complexTab.icon,
          title: complexTab.data.title,
          active:
            complexTab.data.isActive || menuOpened.value[complexTab.name],
          showCondition: true,
          subItems: complexTabsData.value[complexTab.name]
            .filter((data) => !data.hideInMenu)
            .map(
              (data) =>
                ({
                  type: MainMenuLayoutType.tab,
                  key: data.name,
                  title: data.title,
                  active: data.name === computedType.value,
                  showCondition: !data.hideInMenu,
                  link: handleComplexMenuClickCb(data)(),
                  click: (event: MouseEvent) =>
                    handleComplexMenuClick(event, data),
                }) as MainMenuLayoutTab,
            ),
        }) as MainMenuLayoutTab,
    ),
  );

  const isShowCustomGroup = computed(() => customGroupsShowCondition.value);

  const curentCustomGroup = computed(() =>
    complexTabsData.value.custom
      ? complexTabsData.value.custom.find((item) =>
          item.name.includes(route.params.type as string),
        )
      : null,
  );

  const customTabsLayoutData = computed((): MainMenuLayoutTab => ({
    type: MainMenuLayoutType.tabCustom,
    key: 'custom',
    icon: 'fas fa-cogs',
    title: curentCustomGroup.value
      ? curentCustomGroup.value.title
      : t('Custom'),
    active: !!curentCustomGroup.value || menuOpened.value['custom'],
    showCondition: !!isShowCustomGroup.value,
    subItems: complexTabsData.value.custom.map(
      (data) =>
        ({
          type: MainMenuLayoutType.tabCustom,
          key: data.name,
          title: data.title,
          active: data.name === computedType.value,
          showCondition: !data.hideInMenu,
          link: getLink(data.name, {
            isReport: isReport.value,
          }),
          click: (event: MouseEvent) => {
            handleMenuClick(
              event,
              getLink(data.name, {
                isReport: isReport.value,
              }),
            );
          },
          meta: data.meta,
        }) as MainMenuLayoutTab,
    ),
  }));

  const customTabsLayout = computed((): MainMenuLayoutTab[] =>
    isShowCustomGroup.value ? [customTabsLayoutData.value] : [],
  );

  const genDelimiter = (): MainMenuLayoutTab => ({
    key: 'delimiter_1',
    type: MainMenuLayoutType.delimiter,
  });

  const reportTabsLayout = computed((): MainMenuLayoutTab[] =>
    mainPlainLayout.value
      .concat(genDelimiter())
      .concat(conversionsTabLayout.value)
      .concat(postbacksTabLayout.value)
      .concat(complexTabsLayout.value)
      .concat(customTabsLayout.value)
      .concat(errorsTabLayout.value),
  );

  const resolvedMenuType = computed((): string | undefined => {
    const metaType = route.meta?.menuType as string | undefined;
    if (metaType) {
      return metaType;
    }
    if (route.path.startsWith('/app/profile')) {
      return 'settings';
    }
    return undefined;
  });

  const storybookTabsLayout = computed((): MainMenuLayoutTab[] => {
    const result: MainMenuLayoutTab = {
      type: MainMenuLayoutType.tab,
      key: 'storybook',
      icon: 'fas fa-tachometer-alt',
      title: t('Dashboard stub'),
      active: route.path.includes('/app/storybook/'),
      showCondition: false,
      link: getDashboardLink(),
      click: () => {
        router.push(getDashboardLink());
      },
    };

    return [result];
  });

  const getRouteParams = (link: RouteLocationRaw) => ({
    link,
    click: () => {
      router.push(link);
    },
  });

  const dashboardTabsLayout = computed(() => dashboardTabLayout.value);

  const settingsTabsLayout = computed((): MainMenuLayoutTab[] => [
    {
      type: MainMenuLayoutType.tab,
      key: 'general',
      icon: 'fas fa-cog',
      title: t('General'),
      active: route.name === 'app.profile',
      showCondition: true,
      ...getRouteParams({ name: 'app.profile' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'security',
      icon: 'fas fa-shield-alt',
      title: t('Security'),
      active: route.name === 'app.profile.security',
      showCondition: true,
      ...getRouteParams({ name: 'app.profile.security' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'domains',
      icon: 'fas fa-globe-asia',
      title: t('Domains'),
      active: route.name === 'app.profile.domains',
      showCondition: canAccessPage('profile.domains'),
      ...getRouteParams({ name: 'app.profile.domains' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'tracking-links',
      icon: 'fas fa-link',
      title: t('Tracking URLs'),
      active: route.name === 'app.profile.tracking-links',
      showCondition: true,
      ...getRouteParams({ name: 'app.profile.tracking-links' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'upload-conversions',
      icon: 'fas fa-cloud-upload-alt',
      title: t('Upload Conversions'),
      active: route.name === 'app.profile.upload-conversions',
      showCondition: canAccessPage('profile.upload-conversions'),
      ...getRouteParams({ name: 'app.profile.upload-conversions' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'bot-filters',
      icon: 'fas fa-ban',
      title: t('Bot Filters'),
      active: route.name === 'app.profile.bot-filters',
      showCondition: canAccessPage('profile.bot-filters'),
      ...getRouteParams({ name: 'app.profile.bot-filters' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'subscription',
      icon: 'fas fa-credit-card',
      title: t('Subscription'),
      active: route.name === 'app.profile.subscription',
      showCondition: canAccessPage('profile.subscription'),
      ...getRouteParams({ name: 'app.profile.subscription' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'referral-program',
      icon: 'fas fa-users',
      title: t('Referral Program'),
      active: route.name === 'app.profile.referral-program',
      showCondition: canAccessPage('profile.referral-program'),
      ...getRouteParams({ name: 'app.profile.referral-program' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'integrations',
      icon: 'fas fa-file-import',
      title: t('Integrations'),
      active: route.name === 'app.profile.integrations',
      showCondition: canAccessPage('profile.integrations'),
      ...getRouteParams({ name: 'app.profile.integrations' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'multi-user-access',
      icon: 'fas fa-users-cog',
      title: t('Multi-User Access'),
      active: route.name === 'app.profile.multi-user-access',
      showCondition: canAccessPage('profile.multi-user-access'),
      ...getRouteParams({ name: 'app.profile.multi-user-access' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'custom-conversions',
      icon: 'fas fa-file-invoice-dollar',
      title: t('Custom Conversions'),
      active: route.name === 'app.profile.custom-conversions',
      showCondition: canAccessPage('profile.custom-conversions'),
      ...getRouteParams({ name: 'app.profile.custom-conversions' }),
    },
    {
      type: MainMenuLayoutType.tab,
      key: 'plan-description',
      icon: 'fas fa-file-chart-line',
      title: t('Plans Description'),
      active: route.name === 'app.profile.plan-description',
      showCondition: canAccessPage('profile.plan-description'),
      ...getRouteParams({ name: 'app.profile.plan-description' }),
    },
  ]);

  const tabsLayout = computed((): MainMenuLayoutTab[] => {
    switch (resolvedMenuType.value) {
      case 'dashboard':
        return dashboardTabsLayout.value;
      case 'settings':
        return settingsTabsLayout.value;
      case 'storybook':
        return storybookTabsLayout.value;
      default:
        return reportTabsLayout.value;
    }
  });

  const tabsLayoutFlat = computed(() => {
    const result: { [type: string]: MainMenuLayoutTab } = {};
    tabsLayout.value.forEach((item) => {
      if (!item.subItems) {
        result[item.key] = item;
      } else {
        item.subItems.forEach((subItem) => {
          result[subItem.key] = subItem;
        });
      }
    });
    return result;
  });

  const activeItem = computed((): MainMenuLayoutTab => {
    const result = tabsLayout.value
      .filter((item) => item.active)
      .map((item) => {
        if (!item.subItems) {
          return item;
        }
        return item.subItems
          .filter((subItem) => subItem.active)
          .reduce(
            (acc, subItem) => ({
              ...subItem,
              icon: item.icon,
            }),
            null as MainMenuLayoutTab | null,
          );
      })
      .reduce(
        (acc, item) => item,
        null as MainMenuLayoutTab | null,
      );

    return result ? result : tabsLayout.value[0];
  });

  return {
    menuOpened,
    mainMenuLayoutTypeEnum,
    tabsLayout,
    tabsLayoutFlat,
    tabsPlainTabsDataExclude,
    campaignAsFirstTableFilterCondition,
    activeItem,
    isDashboard,
    handleMenuClick,
    handleComplexMenuClick,
  };
};
